import { useEffect, useRef, useSyncExternalStore } from "react";

import { useUnleashClient } from "@unleash/proxy-client-react";
import type { UnleashClient } from "unleash-proxy-client";

import {
  type AccountContextProps,
  useAccountContext,
} from "~/v2/providers/AccountProvider";
import { useAuthContext } from "~/v2/providers/AuthProvider";
import { FeatureFlags } from "./types";
import { UNLEASH_CONFIGURED } from "./unleash";

interface UnleashContextStatus {
  isSynchronized: boolean;
  hasSynchronizationError: boolean;
  isAdvancedTradingDashboardEnabled: boolean;
}

type UnleashContextSyncState = "pending" | "ready" | "error";

interface UnleashContextSyncSnapshot {
  state: UnleashContextSyncState;
  contextKey?: string;
  isAdvancedTradingDashboardEnabled?: boolean;
}

let contextSyncSnapshot: UnleashContextSyncSnapshot = {
  state: UNLEASH_CONFIGURED ? "pending" : "ready",
};
const contextSyncListeners = new Set<() => void>();
const clientStartPromises = new WeakMap<UnleashClient, Promise<void>>();
const SYNCHRONIZATION_RETRY_DELAY_MS = 5_000;

const getContextKey = (
  currentAccount: AccountContextProps["currentAccount"],
  product: AccountContextProps["product"],
  email: AccountContextProps["email"],
) =>
  [
    email,
    currentAccount?.id,
    currentAccount?.account_number,
    currentAccount?.account_type,
    currentAccount?.status,
    currentAccount?.trading_type,
    currentAccount?.elite_status,
    product,
  ].join("|");

const setContextSyncState = (
  state: UnleashContextSyncState,
  contextKey?: string,
  isAdvancedTradingDashboardEnabled?: boolean,
) => {
  if (
    contextSyncSnapshot.state === state &&
    contextSyncSnapshot.contextKey === contextKey &&
    contextSyncSnapshot.isAdvancedTradingDashboardEnabled ===
      isAdvancedTradingDashboardEnabled
  ) {
    return;
  }
  contextSyncSnapshot = {
    state,
    contextKey,
    isAdvancedTradingDashboardEnabled,
  };
  for (const listener of contextSyncListeners) {
    listener();
  }
};

const ensureClientStarted = (client: UnleashClient): Promise<void> => {
  const existingStart = clientStartPromises.get(client);
  if (existingStart) {
    return existingStart;
  }

  const start = client.start().catch((error: unknown) => {
    clientStartPromises.delete(client);
    throw error;
  });
  clientStartPromises.set(client, start);
  return start;
};

const subscribeToContextSync = (listener: () => void) => {
  contextSyncListeners.add(listener);
  return () => contextSyncListeners.delete(listener);
};

export const useUnleashContextStatus = (): UnleashContextStatus => {
  const {
    currentAccount,
    product,
    email,
    isLoading,
    liveAccounts,
    paperAccounts,
  } = useAccountContext();
  const snapshot = useSyncExternalStore(
    subscribeToContextSync,
    () => contextSyncSnapshot,
    () => contextSyncSnapshot,
  );
  const hasAccounts = liveAccounts.length > 0 || paperAccounts.length > 0;
  const contextKey = getContextKey(currentAccount, product, email);
  const hasCompleteContext =
    !isLoading &&
    currentAccount !== undefined &&
    product !== undefined &&
    email !== undefined;
  const appliesToCurrentContext =
    hasCompleteContext && snapshot.contextKey === contextKey;

  if (!UNLEASH_CONFIGURED) {
    return {
      isSynchronized: true,
      hasSynchronizationError: false,
      isAdvancedTradingDashboardEnabled: false,
    };
  }

  if (!isLoading && !hasAccounts) {
    return {
      isSynchronized: false,
      hasSynchronizationError: true,
      isAdvancedTradingDashboardEnabled: false,
    };
  }

  return {
    isSynchronized: appliesToCurrentContext && snapshot.state === "ready",
    hasSynchronizationError:
      appliesToCurrentContext && snapshot.state === "error",
    isAdvancedTradingDashboardEnabled:
      appliesToCurrentContext &&
      snapshot.state === "ready" &&
      snapshot.isAdvancedTradingDashboardEnabled === true,
  };
};

/**
 * Syncs account and user context to the Unleash SDK.
 *
 * Must be rendered inside both FlagProvider and AccountProvider.
 * Updates Unleash context when the user switches accounts or products,
 * enabling per-user/per-account targeting strategies.
 */
const UnleashContextSyncInner = () => {
  const client = useUnleashClient();
  const { authStatus, isAuthenticated } = useAuthContext();
  const { currentAccount, product, email, isLoading } = useAccountContext();
  const prevContextKey = useRef<string>();

  useEffect(() => {
    if (authStatus === "loading") {
      setContextSyncState("pending");
      return;
    }

    if (!isAuthenticated) {
      prevContextKey.current = undefined;
      setContextSyncState("pending");
      void ensureClientStarted(client).catch(() => undefined);
      return;
    }

    if (
      isLoading ||
      currentAccount === undefined ||
      product === undefined ||
      email === undefined
    ) {
      prevContextKey.current = undefined;
      setContextSyncState("pending");
      return;
    }

    const contextKey = getContextKey(currentAccount, product, email);

    if (contextKey === prevContextKey.current) {
      return;
    }
    prevContextKey.current = contextKey;

    setContextSyncState("pending", contextKey);
    let cancelled = false;
    let synchronizationSettled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let stopListening = () => undefined;
    const context = {
      userId: email,
      properties: {
        account_id: currentAccount.id,
        account_number: currentAccount.account_number ?? "",
        account_type: currentAccount.account_type ?? "",
        account_status: currentAccount.status,
        account_trading_type: currentAccount.trading_type,
        elite_status: currentAccount.elite_status ?? "",
        product,
      },
    };

    const finishSynchronization = () => {
      if (cancelled || !synchronizationSettled) {
        return;
      }
      if (client.getError()) {
        setContextSyncState("error", contextKey);
      } else {
        if (retryTimeout) {
          clearTimeout(retryTimeout);
          retryTimeout = undefined;
        }
        setContextSyncState(
          "ready",
          contextKey,
          client.isEnabled(FeatureFlags.ADVANCED_TRADING_DASHBOARD),
        );
        stopListening();
      }
    };
    const handleUpdate = () => {
      finishSynchronization();
    };
    const handleError = () => finishSynchronization();
    stopListening = () => {
      client.off("update", handleUpdate);
      client.off("error", handleError);
    };

    client.on("update", handleUpdate);
    client.on("error", handleError);
    const synchronize = async () => {
      const existingStart = clientStartPromises.get(client);
      if (!existingStart && !client.isReady()) {
        // Seed the context before starting so the SDK's first request is never
        // an anonymous evaluation for an authenticated user.
        await client.updateContext(context);
        await ensureClientStarted(client);
        return;
      }
      if (existingStart && !client.isReady()) {
        await existingStart;
      }
      await client.updateContext(context);
    };

    const runSynchronization = () => {
      void synchronize().then(
        () => {
          synchronizationSettled = true;
          finishSynchronization();
        },
        () => {
          synchronizationSettled = true;
          if (!cancelled) {
            setContextSyncState("error", contextKey);
            retryTimeout = setTimeout(() => {
              retryTimeout = undefined;
              if (cancelled) {
                return;
              }
              synchronizationSettled = false;
              setContextSyncState("pending", contextKey);
              runSynchronization();
            }, SYNCHRONIZATION_RETRY_DELAY_MS);
          }
        },
      );
    };

    runSynchronization();

    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      stopListening();
    };
  }, [
    authStatus,
    client,
    currentAccount,
    product,
    email,
    isAuthenticated,
    isLoading,
  ]);

  return null;
};

/**
 * Wrapper that only renders the sync component when Unleash is configured.
 * Safe to render unconditionally in the component tree.
 */
export const UnleashContextSync = () => {
  if (!UNLEASH_CONFIGURED) {
    return null;
  }
  return <UnleashContextSyncInner />;
};
